import { z } from "zod";

/** Day of week, JS convention: 0 = Sunday … 6 = Saturday. */
export const dayOfWeekSchema = z.number().int().min(0).max(6);

/** HH:MM 24h (also accepts HH:MM:SS, normalised to HH:MM:SS by the API). */
const timeSchema = z
	.string()
	.regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, "Invalid time (HH:MM)");

export const doctorScheduleSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	member_id: z.string().uuid(),
	day_of_week: dayOfWeekSchema,
	start_time: z.string(),
	end_time: z.string(),
	break_start: z.string().nullable(),
	break_end: z.string().nullable(),
	slot_granularity_minutes: z.number().int().positive(),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type DoctorScheduleApi = z.infer<typeof doctorScheduleSchema>;

export const scheduleDayInputSchema = z
	.object({
		day_of_week: dayOfWeekSchema,
		start_time: timeSchema,
		end_time: timeSchema,
		break_start: timeSchema.nullable().optional(),
		break_end: timeSchema.nullable().optional(),
		slot_granularity_minutes: z.number().int().positive().max(120).default(15),
		is_active: z.boolean().default(true),
	})
	.refine((d) => d.start_time < d.end_time, {
		message: "end_time must be after start_time",
		path: ["end_time"],
	})
	.refine(
		(d) =>
			(d.break_start == null && d.break_end == null) ||
			(d.break_start != null &&
				d.break_end != null &&
				d.break_start < d.break_end),
		{ message: "break_end must be after break_start", path: ["break_end"] },
	);
export type ScheduleDayInput = z.infer<typeof scheduleDayInputSchema>;

export const setWeeklyScheduleInputSchema = z.object({
	days: z.array(scheduleDayInputSchema).max(7),
	/** If true, days not present in `days` are deactivated (closed). */
	replace: z.boolean().default(false),
});
export type SetWeeklyScheduleInput = z.infer<
	typeof setWeeklyScheduleInputSchema
>;

export const availableSlotsQuerySchema = z.object({
	member_id: z.string().uuid(),
	date: z.string().date(),
	duration_minutes: z.coerce.number().int().positive().max(480).default(30),
	buffer_minutes: z.coerce.number().int().nonnegative().max(60).default(0),
});
export type AvailableSlotsQuery = z.infer<typeof availableSlotsQuerySchema>;

export interface AvailableSlot {
	start_at: string; // ISO
	end_at: string; // ISO
}

export interface AvailableSlotsResponse {
	date: string;
	day_of_week: number;
	member_id: string;
	duration_minutes: number;
	buffer_minutes: number;
	working_hours: {
		start_time: string;
		end_time: string;
		break_start: string | null;
		break_end: string | null;
	} | null;
	/** True if no schedule row existed and we used the fallback. */
	using_default_schedule: boolean;
	slots: AvailableSlot[];
}

/**
 * Sensible fallback when a member has no schedule row for the requested day.
 * Iraqi clinic pattern: Sun..Thu 10:00-20:00 with 14:00-16:00 break, Fri/Sat closed.
 * Clinics customise via PUT /scheduling/members/:id/schedule.
 */
export function defaultScheduleForDay(
	dayOfWeek: number,
): ScheduleDayInput | null {
	if (dayOfWeek === 5 || dayOfWeek === 6) return null; // Fri & Sat closed
	return {
		day_of_week: dayOfWeek,
		start_time: "10:00",
		end_time: "20:00",
		break_start: "14:00",
		break_end: "16:00",
		slot_granularity_minutes: 15,
		is_active: true,
	};
}
